import React, { useState, useEffect } from 'react';
import { Bell, X, Info, AlertCircle, Calendar, Mail, CheckCircle, Clock, MessageSquare, Zap } from 'lucide-react';
import { db, UserEntry } from '../../db';
import { syncManager } from '../../services/sync';
import { config } from '../../config';

interface NotificationItem {
    id: string;
    type: string;
    title: string;
    description: string;
    original: string;
    time: string;
    icon: JSX.Element;
    color: string;
    confidence: number;
}

const NotificationCenter = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [userMetadata, setUserMetadata] = useState<UserEntry | null>(null);

    useEffect(() => {
            db.user.toCollection().first().then(user => setUserMetadata(user ?? null));
        }, []);

    useEffect(() => {
        if (!isOpen) {return;}

        const loadSwaps = async () => {
            setLoading(true);
            try {
                // 1. Fetch latest from HQ (if Admin or Coach)
                const isPrivileged = userMetadata?.role === 'ADMIN' || userMetadata?.role === 'COACH' || config.USER_EMAIL === config.ADMIN_EMAIL;
                if (isPrivileged) {
                    await syncManager.fetchTabData('Scripts_Swap_File', config.ADMIN_EMAIL);
                }

                // 2. Load from local cache
                const swaps = await syncManager.fetchTabData('Scripts_Swap_File', config.ADMIN_EMAIL);

                if (swaps && Array.isArray(swaps)) {
                    console.log("🔔 NotificationCenter: Loaded Swaps:", swaps.length, swaps);
                    const formatted = swaps.slice(0, 10).map((s, idx) => ({
                        id: `swap-${idx}`,
                        type: 'swap',
                        title: `Swap Suggestion: ${s.Client_Email}`,
                        description: s.Suggested_Swap,
                        original: s.Original_Message,
                        time: new Date(s.Timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        icon: <Mail size={16} />,
                        color: 'blue',
                        confidence: s.Confidence_Score
                    }));
                    setNotifications(formatted);
                } else {
                    console.warn("🔔 NotificationCenter: No swaps found or invalid format", swaps);
                }
            } catch (err) {
                console.error("Failed to load script swaps:", err);
            } finally {
                setLoading(false);
            }
        };

        loadSwaps();
    }, [isOpen]);

    if (!isOpen) {return null;}

    return (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-2xl border-l border-slate-100 flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-3">
                    <div className="bg-slate-900 p-2 rounded-xl text-white">
                        <Bell size={18} />
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest leading-none">Notifications</h3>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Recent Activity</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400">
                    <X size={20} />
                </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {notifications.length > 0 ? (
                    notifications.map(notif => (
                        <div key={notif.id} className="group p-4 bg-white border border-slate-100 rounded-2xl hover:border-blue-100 hover:shadow-sm transition-all cursor-pointer">
                            <div className="flex gap-4">
                                <div className={`mt-1 p-2 rounded-lg bg-${notif.color}-50 text-${notif.color}-600`}>
                                    {notif.icon}
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start mb-1">
                                        <p className="text-[10px] font-black text-slate-900 uppercase tracking-wider">{notif.title}</p>
                                        <span className="text-[9px] text-slate-300 font-bold">{notif.time}</span>
                                    </div>

                                    {notif.original && (
                                        <p className="text-[10px] text-slate-400 italic mb-2 line-clamp-1 border-l-2 border-slate-100 pl-2">
                                            "{notif.original}"
                                        </p>
                                    )}

                                    <p className="text-xs font-bold text-blue-600 leading-relaxed bg-blue-50/50 p-2 rounded-lg border border-blue-100/50">
                                        {notif.description}
                                    </p>

                                    {notif.confidence && (
                                        <div className="mt-2 flex items-center gap-1">
                                            <Zap size={10} className="text-amber-400 fill-amber-400" />
                                            <span className="text-[8px] font-black text-slate-400 uppercase">Match: {(notif.confidence * 100).toFixed(0)}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                        <div className="bg-slate-50 p-6 rounded-full text-slate-300 mb-4">
                            <Info size={40} />
                        </div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No new notifications</p>
                    </div>
                )}
            </div>

            {/* Bottom Panel */}
            <div className="p-4 border-t border-slate-50">
                <button className="w-full py-3 bg-slate-50 text-slate-400 hover:text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">
                    Clear All History
                </button>
            </div>
        </div>
    );
};

export default NotificationCenter;
